<template>
  <div class="column">
    <v-btn
      prepend-icon="mdi-help-circle-outline"
      color="primary"
      variant="outlined"
      density="compact"
      @click="helpModalOpen = true"
    >
      Hilfe
    </v-btn>
    <CommonDialog v-model:is-active="helpModalOpen">
      <template #header>
        <header class="d-flex align-center justify-space-between w-100">
          <p class="modal-card-title">Anleitung für Equipment Config Editor</p>
          <v-btn
            class="delete"
            aria-label="close"
            color="primary"
            variant="outlined"
            icon="mdi-close"
            @click="helpModalOpen = false"
          />
        </header>
      </template>
      <template #content>
        <section class="px-4">
          Die Equipment Config definiert wie das Formular für Empfänger/Sender
          von diesem Equipment Typ aussieht und was der Benutzer eingeben kann.
          Diese Konfiguration ist als JSON definiert. Die Gesamtstruktur der
          Konfiguration sieht wie folgt aus:
          <p class="code-wrapper">
            <codemirror v-model="configStructure" />
          </p>

          <ul>
            <li><code>title</code>: Titel für Config. Type: String</li>
            <li>
              <code>model</code>: Datenmodell. Werte die hier definiert sind,
              werden als default benutzt. Type: Object
            </li>
            <li><code>tabs</code>: Einzelne Tabs. Type: Array von Tabs.</li>
            <li>
              <code>displayOrder</code>: Reihenfolge der Felder für Darstellung
              (optional). Type: Array von Strings (= keys der Felder).
            </li>
          </ul>

          Innerhalb des <code>tabs</code> arrays, können einzelne Tabs
          hinzugefügt werden. Ein Tab hat folgende Struktur:
          <p class="code-wrapper">
            <codemirror v-model="tabStructure" />
          </p>

          <ul>
            <li><code>name</code>: Tab Name. Type: String</li>
            <li>
              <code>icon</code>: Tab Icon. Beispiel
              <code>"mdi mdi-fountain"</code> .Icons von dieser List:
              https://cdn.materialdesignicons.com/4.0.96/ Type: String
            </li>
            <li>
              <code>fields</code>: Einzelne Felder. Type: Array von Feidls.
              Siehe unten
            </li>
          </ul>

          Innerhalb des <code>fields</code> arrays, können einzelne Felder
          hinzugefügt werden. Ein Feld hat folgende Struktur:
          <p class="code-wrapper">
            <codemirror v-model="fieldStructure" />
          </p>

          <ul>
            <li>
              <code>key</code>: Feld key, entspricht key im <code>model</code>.
              Type: String
            </li>
            <li>
              <code>type</code>: Feldtyp, siehe unten für verfügbare Typen.
              Type: String
            </li>
            <li><code>properties</code>: Beschreibt das Feld. Type: object</li>
            <li>
              <code>properties.label</code>: Bezeichnung für Feld. Type: String
            </li>
            <li>
              <code>properties.extraData</code>: Zusätzliche Daten für dieses
              Feld, welche nicht veränderbar sind und von User nicht gesehen
              werden. Type: Object
            </li>
            <ul>
              <li>
                <code>dataID</code>: Definiert position dieses Feldes innerhalb
                der Konfiguration
              </li>
              <li>
                <code>internalName</code>: Feld Bezeichnung die intern verwendet
                werden soll (e.g. eGate Export)
              </li>
              <li>
                <code>externalName</code>: Feld Bezeichnung die für Benutzer
                verwendet werden soll (e.g. Planer-Anordnung)
              </li>
              <li>
                <code>updateLinkedOnChange</code>: Boolean Feld: Falls true,
                werden alle verlinkted Empfänger auf 'zu aktualisieren' gesetzt,
                wenn sich dieses Feld ändert (default: false)
              </li>
            </ul>
          </ul>

          Die einzelnen Felder sind in der folgenden Tabelle beschrieben:

          <v-data-table
            :headers="headers"
            :items="fieldTypes"
            class="elevation-1"
          >
            <template #item.type="{ item }">
              {{ item.type }}
            </template>
            <template #item.structure="{ item }">
              <p class="code-wrapper">
                <codemirror v-model="item.structure" />
              </p>
            </template>
            <template #item.description="{ item }">
              {{ item.description }}
            </template>
          </v-data-table>

          <v-data-table
            :data="fieldTypes"
            :headers="headers"
            striped
            bordered
            hoverable
          >
            <template #item.type="{ item }"> {{ item.type }}</template>
            <template #item.structure="{ item }">
              <p class="code-wrapper">
                <codemirror v-model="item.structure" />
              </p>
            </template>
            <template #item.description="{ item }">
              {{ item?.description }}
            </template>
          </v-data-table>

          <p>
            Im <code>displayOrder</code> Array kann die Reihenfolge der Felder
            für die Darstellung definiert werden. <code>displayOrder</code> ist
            ein Array von <code>key</code> der einzelnen Felder.
          </p>

          Eine Beispielskonfiguration ist unten angefügt:
          <p class="code-wrapper">
            <Codemirror v-model="sampleConfig" />
          </p>
        </section>
      </template>
    </CommonDialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Codemirror } from 'vue-codemirror'

import CommonDialog from '@/components/common/CommonDialog.vue'

const helpModalOpen = ref(false)
const headers = [
  {
    key: 'type',
    title: 'Typ',
  },
  {
    key: 'structure',
    title: 'Struktur',
  },
  {
    key: 'description',
    title: 'Beschreibung',
  },
]
const fieldTypes = [
  {
    type: 'label',
    structure: `
            {
              "type": "label-field",
              "key": "",
              "properties": {
                "text": "",
                "label": "",
                "tooltip": ""
              }
            }
          `,
    description: 'Info Text Feld.',
  },
  {
    type: 'checkbox-field',
    structure: `
           {
              "key": "",
              "type": "checkbox-field",
              "properties": {
                "label": "",
                "extraData": {}
              }
            }
          `,
    description: 'Checkbox.',
  },
  {
    type: 'input',
    structure: `
            {
              "key": "",
              "type": "input",
              "properties": {
                "label": "",
                "maxlength": 3,
                "extraData": {}
              }
            }
          `,
    description: 'Input',
  },
  {
    type: 'number-field',
    structure: `
            {
              "properties": {
                "extraData": {
                  "dataID": 1,
                  "internalName": "Place",
                  "externalName": "Platzierung"
                },
                "label": "Platz",
                "inputType": "number",
                "min": 0,
                "max": 200
              },
              "type": "number-field",
              "key": "position"
            }
          `,
    description: `Number input. Beachte: Das Model muss nun eine Zahl sein. Die Felder "min" und "max" sind optional`,
  },
  {
    type: 'selection',
    structure: `
            {
              "key": "",
              "type": "selection",
              "properties": {
                "label": "Simple",
                "extraData": {}
              },
              "templateOptions": {
                "options": [
                  { "text": "Jesse", "value": "jesse" },
                  { "text": "John", "value": "john" }
                ],
              }
            }
          `,
    description: 'Auswahlfeld (Dropdown)',
  },
  {
    type: 'sww',
    structure: `
            {
              "key": "sww",
              "type": "sww-field",
              // Optional:
              "properties": {
                "extraData": {
                  "dependencyKey": "chef_fe"
                }
              }
            }
          `,
    description:
      'Beachte: "key" muss auf "sww" gesetzt werden (reserviertes keyword). SWW Feld. Model sollte als "null" gesetzt werden für leere Config. Durch das Setzen des "dependencyKey" Wertes unter "extraData" kann eine Abhängigkeit von einer Checkbox gesetzt werden.',
  },
  {
    type: 'maxflex',
    structure: `
            {
              "key": "maxflex",
              "type": "maxflex-field",
            }
          `,
    description:
      'Beachte: "key" muss auf "maxflex" gesetzt werden (reserviertes keyword). NUR FÜR MAXFLEX ZU BENUTZEN.',
  },
  {
    type: 'egate',
    structure: `
           {
              "type": "egate-field",
              "key": "egate"
            }
          `,
    description:
      'Beachte: "key" muss auf "egate" gesetzt werden (reserviertes keyword). NUR FÜR eGate ZU BENUTZEN.',
  },
]

const configStructure = `
      {
        "title": "",
        "model": {},
        "tabs": [],
        "displayOrder": []
      }
    `

const tabStructure = `
      {
        "name": "",
        "icon": "",
        "fields": []
      }
    `

const fieldStructure = `
      {
        "key": "",
        "type": "",
        "properties": {
          ...,
          "label": "",
          "extraData": {}
        }
      }
    `

const sampleConfig = `
      {
        "title": "title",
        "model": {
          "key2": "Default",
          "name": "jesse"
        },
        "tabs": [
          {
            "name": "name",
            "icon": "mdi mdi-fountain",
            "fields": [
              {
                    "key": "key2",
                    "type": "input",
                    "properties": {
                      "label": "Test Label",
                      "extraData": {
                        "dataID": 2,
                        "internalName": "Internal test",
                        "externalName": "External label"
                      }
                    }
                  }
            ]
          },
          {
            "name": "name",
            "icon": "",
            "fields": [
              {
                "key": "name",
                "type": "selection",
                "properties": {
                  "label": "Simple",
                  "extraData": {
                    "dataID": 1,
                    "internalName": "Internal test",
                    "externalName": "External label",
                    "updateLinkedOnChange": true
                  }
                },
                "templateOptions": {
                  "options": [
                    { "text": "Jesse", "value": "jesse" },
                    { "text": "John", "value": "john" }
                  ]
                }
              }
            ]
          }
        ],
        "displayOrder": [
          "name",
          "key2"
        ]
      }
    `
</script>
<style lang="scss" scoped>
.code-wrapper > pre {
  padding: 0;
}

.equipment-help-modal {
  width: 950px;
}

:deep(.cm-scroller) {
  overflow: 'auto';
}

:deep(.cm-editor) {
  max-height: 400px;
  background-color: #f5f5f5;
}
</style>
